// Doctor detail — provider credentials (NPI, license + expiry, malpractice, agreement/BAA). These
// are identifiers/compliance metadata, NOT clinical data.
import { Link, useParams } from "react-router-dom";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { formatDate } from "../lib/format";
import { Loading, ErrorState, EmptyState, PageHeader, StatusBadge } from "../components/ui";
import { DoctorActions } from "../components/DoctorActions";

function initials(a?: string, b?: string) {
  return `${(a || "D").charAt(0)}${(b || "").charAt(0)}`.toUpperCase();
}

export function DoctorDetail() {
  const { id = "" } = useParams();
  const api = useApi();
  const { data, loading, error, reload } = useAsync(() => api.doctor(id), [id]);

  // Only show the full-page loader on the INITIAL load. On reload() after a write, keep the existing
  // content (and the actions panel) mounted so its success message + inputs aren't wiped.
  if (loading && !data) return <Loading />;
  if (error && !data) return <ErrorState message={error} />;
  if (!data) return <ErrorState message="Doctor not found." />;
  const d = data.doctor;
  const c = data.credentials;

  return (
    <div className="page cmd-detail">
      <PageHeader title={`${d.first_name} ${d.last_name}`} sub={d.email} />
      <Link to="/doctors" className="back-link">
        ← All doctors
      </Link>

      <div className="settings-card detail-identity">
        <div className="detail-identity__head">
          <div className="detail-avatar">{initials(d.first_name, d.last_name)}</div>
          <div>
            <div className="detail-name">{d.first_name} {d.last_name}</div>
            <div className="detail-email">{d.email}</div>
            <div className="detail-tags">
              {d.specialty && <span className="tag">{d.specialty}</span>}
              <StatusBadge value={d.application_status} />
            </div>
          </div>
        </div>
      </div>

      <div className="settings-card">
        <div className="pcard-head"><h3>Integration <em>identity</em></h3></div>
        <dl className="kv">
          <div><dt>Healthie provider</dt><dd className="mono">{d.healthie_provider_id ?? "—"}</dd></div>
          <div><dt>Zoom host</dt><dd className="mono">{d.zoom_user_id ?? "—"}</dd></div>
        </dl>
      </div>

      <DoctorActions doctor={{ ...d, id }} onChanged={reload} />

      <div className="settings-card">
        <div className="pcard-head"><h3>Provider <em>credentials</em></h3></div>
        {c ? (
          <dl className="kv">
            <div>
              <dt>NPI</dt>
              <dd className="mono">{c.npi}</dd>
            </div>
            <div>
              <dt>DEA</dt>
              <dd className="mono">{c.dea_number ?? "—"}</dd>
            </div>
            <div>
              <dt>License</dt>
              <dd className="mono">
                {c.license_state} {c.license_number}
              </dd>
            </div>
            <div>
              <dt>License expires</dt>
              <dd>{formatDate(c.license_expires_at)}</dd>
            </div>
            <div>
              <dt>Malpractice carrier</dt>
              <dd>{c.malpractice_carrier ?? "—"}</dd>
            </div>
            <div>
              <dt>Malpractice policy</dt>
              <dd className="mono">{c.malpractice_policy_no ?? "—"}</dd>
            </div>
            <div>
              <dt>Malpractice expires</dt>
              <dd>{formatDate(c.malpractice_expires_at)}</dd>
            </div>
            <div>
              <dt>Agreement</dt>
              <dd>
                {c.agreement_version ?? "—"} · {formatDate(c.agreement_accepted_at)}
              </dd>
            </div>
            <div>
              <dt>BAA accepted</dt>
              <dd>{formatDate(c.baa_accepted_at)}</dd>
            </div>
          </dl>
        ) : (
          <EmptyState>No credentials on file.</EmptyState>
        )}
      </div>
    </div>
  );
}
